import { useEffect, useMemo, useRef, useState } from "react";
import { resolveImageGeneratorResultSrc } from "../../app/api/imageGenerator";

type PreviewItem = {
  id: string;
  imageUrl: string | null;
};

const useImageGeneratorPreviews = (items: PreviewItem[]) => {
  const [previews, setPreviews] = useState<Record<string, string>>({});
  const cacheRef = useRef<Map<string, string>>(new Map());
  const itemsRef = useRef(items);
  itemsRef.current = items;
  const signature = useMemo(
    () => items.map(item => `${item.id}:${item.imageUrl || ""}`).join("|"),
    [items]
  );

  useEffect(() => {
    let cancelled = false;
    const cache = cacheRef.current;
    const currentItems = itemsRef.current;
    const needed = new Set(
      currentItems
        .map(item => item.imageUrl)
        .filter((url): url is string => Boolean(url))
    );

    (async () => {
      const missing = Array.from(needed).filter(url => !cache.has(url));
      await Promise.all(
        missing.map(async url => {
          try {
            cache.set(url, await resolveImageGeneratorResultSrc(url));
          } catch {
            // Leave uncached so a later signature/effect pass can retry.
          }
        })
      );
      if (cancelled) {
        return;
      }

      Array.from(cache.entries()).forEach(([url, blobUrl]) => {
        if (!needed.has(url)) {
          if (blobUrl.startsWith("blob:")) {
            URL.revokeObjectURL(blobUrl);
          }
          cache.delete(url);
        }
      });

      const next: Record<string, string> = {};
      for (const item of currentItems) {
        if (item.imageUrl && cache.has(item.imageUrl)) {
          next[item.id] = cache.get(item.imageUrl) as string;
        }
      }
      setPreviews(next);

      // Retry once if auth-gated relative urls failed on the first pass.
      const stillMissing = Array.from(needed).filter(url => !cache.has(url));
      if (!stillMissing.length) {
        return;
      }
      await new Promise(resolve => window.setTimeout(resolve, 400));
      if (cancelled) {
        return;
      }
      await Promise.all(
        stillMissing.map(async url => {
          try {
            cache.set(url, await resolveImageGeneratorResultSrc(url));
          } catch {
            // Leave empty; gray tile remains until items change.
          }
        })
      );
      if (cancelled) {
        return;
      }
      const retried: Record<string, string> = {};
      for (const item of itemsRef.current) {
        if (item.imageUrl && cache.has(item.imageUrl)) {
          retried[item.id] = cache.get(item.imageUrl) as string;
        }
      }
      setPreviews(retried);
    })();

    return () => {
      cancelled = true;
    };
  }, [signature]);

  useEffect(
    () => () => {
      cacheRef.current.forEach(blobUrl => {
        if (blobUrl.startsWith("blob:")) {
          URL.revokeObjectURL(blobUrl);
        }
      });
      cacheRef.current.clear();
    },
    []
  );

  return previews;
};

export default useImageGeneratorPreviews;
